import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Loader2, Check, Crown, Clock } from 'lucide-react';

import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import ShareCard from '../components/ShareCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * BookingSuccessPage — confirmation page after successful payment.
 * Route: /booking/success?session_id=... or ?payment_intent=...&booking_id=...
 *
 * Extracted from App.js (Fase 1) — pure relocation, no logic changes.
 */
export const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const paymentIntentId = searchParams.get('payment_intent');

  // For Stripe Hosted Checkout (legacy: uses session_id)
  const { data: paymentStatus, isLoading: sessionLoading } = useQuery({
    queryKey: ['payment', sessionId],
    queryFn: async () => {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      return response.data;
    },
    enabled: !!sessionId,
    refetchInterval: (data) => data?.payment_status === 'paid' ? false : 2000
  });

  // For Embedded Checkout (uses payment_intent from iDEAL/Bancontact redirect)
  const { data: intentStatus, isLoading: intentLoading } = useQuery({
    queryKey: ['payment-intent', paymentIntentId],
    queryFn: async () => {
      const response = await axios.post(`${API}/payments/confirm-embedded`, {
        payment_intent_id: paymentIntentId,
        booking_id: bookingId
      });
      return response.data;
    },
    enabled: !!paymentIntentId && !!bookingId,
    retry: 2
  });

  // Determine if payment is confirmed
  const isPaid = paymentStatus?.payment_status === 'paid' || intentStatus?.success || (!sessionId && !paymentIntentId && !!bookingId);
  const isLoading = sessionId ? sessionLoading : (paymentIntentId ? intentLoading : false);
  const finalAmount = paymentStatus?.amount_total || intentStatus?.booking?.final_price || intentStatus?.booking?.total_price || 0;

  const { data: booking } = useQuery({
    queryKey: ['booking', bookingId],
    queryFn: async () => {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      return response.data;
    },
    enabled: !!bookingId && isPaid
  });

  if (isLoading || (!isPaid && (sessionId || paymentIntentId))) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <h2 className="font-sans font-bold text-2xl mb-2">Processing Payment...</h2>
          <p className="text-muted-foreground">Please wait while we confirm your booking</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="booking-success">
      <div className="max-w-lg mx-auto px-4 text-center">
        {/* Conditional hero — green check if confirmed, amber clock if Sunhotels not yet finalised */}
        {booking?.status === 'confirmation_pending' || intentStatus?.booking?.status === 'confirmation_pending' ? (
          <>
            <div className="w-20 h-20 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-amber-600 dark:text-amber-400" />
            </div>
            <h1 className="font-bold text-3xl mb-3" data-testid="booking-pending-heading">Payment received — finalizing your booking</h1>
            <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700 rounded-lg p-4 mb-6 text-left" data-testid="booking-pending-banner">
              <p className="text-sm text-amber-900 dark:text-amber-100">
                We've received your payment, but the hotel reservation is still being finalised by our team.
                You'll receive a separate email with your hotel voucher within 24 hours.
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                If you don't hear back, please contact support with your booking reference below.
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-primary" />
            </div>

            <h1 className="font-bold text-3xl mb-4">Booking Confirmed!</h1>
            <p className="text-muted-foreground mb-8">
              Your booking has been confirmed. A confirmation email has been sent to your email address.
            </p>
          </>
        )}

        <Card className="p-6 mb-8 text-left">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Booking Reference</span>
              <Badge variant="outline" className="font-mono">{bookingId}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Amount Paid</span>
              <span className="font-semibold">€{(finalAmount || 0).toFixed(2)}</span>
            </div>
            {booking?.confirmation_number && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Confirmation #</span>
                <Badge className="font-mono">{booking.confirmation_number}</Badge>
              </div>
            )}
            {booking?.new_pass_code && (
              <>
                <Separator />
                <div className="bg-accent/20 rounded-lg p-4">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Crown className="w-4 h-4" />
                    Your New Freestays Pass Code:
                  </p>
                  <p className="font-mono text-lg font-semibold">{booking.new_pass_code}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {booking.pass_purchase_type === 'annual' ? 'Valid for 1 year' : 'Used for this booking'}
                  </p>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Share Your Success - Phase 8 */}
        <div className="mb-8">
          <p className="text-sm text-muted-foreground mb-3 text-center">Share your booking with friends!</p>
          <div className="flex justify-center">
            <ShareCard
              type="booking"
              data={{
                hotel: booking?.hotel_name || 'Hotel',
                destination: booking?.destination || ''
              }}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="view-bookings-btn">
            View My Bookings
          </Button>
          <Button variant="outline" onClick={() => navigate('/')} className="rounded-full">
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
